import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, BarChart3, TrendingUp, TrendingDown, Package, Users,
  Loader2, Calendar
} from 'lucide-react';

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await api.get(`/analytics/${user.id}`);
      setAnalytics(response.data);
    } catch (error) {
      // Analytics endpoint may not be wired up yet — fall back to
      // per-role mock data so the dashboard still renders something
      // meaningful in dev.
      console.warn('Analytics API unavailable, using mock data', error);
      // No orders on the platform (PR 17) — no revenue / order-count
      // metrics, including in the dev mock.
      const mockData = {
        manufacturer: {
          total_distributors: 12,
          top_products: [
            { name: 'NPK Fertilizer', sales: 45000, units: 150 },
            { name: 'Pesticide Pro', sales: 32000, units: 80 },
            { name: 'Herbicide Max', sales: 28000, units: 70 },
          ]
        },
        distributor: {
          total_shopkeepers: 25, inventory_value: 45000,
        },
        shopkeeper: {
          total_customers: 45,
        },
        farmer: { total_plots: 4, total_area: 12.5, diagnoses_count: 8, healthy_crops: 75 }
      };
      setAnalytics(mockData[user.user_type] || mockData.farmer);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const renderMetricCard = (title, value, change, icon, prefix = '') => {
    const Icon = icon;
    const isPositive = change >= 0;
    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-farm-primary/10 rounded-full flex items-center justify-center">
              <Icon className="w-5 h-5 text-farm-primary" />
            </div>
            {change !== undefined && (
              <div className={`flex items-center gap-1 text-sm ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
                {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {Math.abs(change)}%
              </div>
            )}
          </div>
          <p className="text-2xl font-bold">{prefix}{typeof value === 'number' && prefix === '$' ? formatCurrency(value).replace('$', '') : value}</p>
          <p className="text-sm text-muted-foreground">{title}</p>
        </CardContent>
      </Card>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-farm-primary" />
        </div>
      );
    }
    if (!analytics) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <BarChart3 className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-muted-foreground">{t('analytics.empty')}</p>
          </CardContent>
        </Card>
      );
    }
    return (
      <>
        {/* Period Selector */}
        <div className="flex items-center gap-2 mb-6">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{t('analytics.period')}</span>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {analytics.total_distributors !== undefined && renderMetricCard(t('analytics.metrics.distributors'), analytics.total_distributors, undefined, Users)}
          {analytics.total_shopkeepers !== undefined && renderMetricCard(t('analytics.metrics.shopkeepers'), analytics.total_shopkeepers, undefined, Users)}
          {analytics.total_customers !== undefined && renderMetricCard(t('analytics.metrics.customers'), analytics.total_customers, undefined, Users)}
          {analytics.inventory_value !== undefined && renderMetricCard(t('analytics.metrics.inventoryValue'), analytics.inventory_value, undefined, Package, '$')}
          {analytics.total_plots !== undefined && renderMetricCard(t('analytics.metrics.totalPlots'), analytics.total_plots, undefined, BarChart3)}
          {analytics.healthy_crops !== undefined && renderMetricCard(t('analytics.metrics.healthyCrops'), `${analytics.healthy_crops}%`, undefined, TrendingUp)}
        </div>

        {/* Top Products (for manufacturers) */}
        {analytics.top_products && (
          <>
            <h2 className="font-semibold text-lg mb-4">{t('analytics.topProducts')}</h2>
            <div className="space-y-3">
              {analytics.top_products.map((product, index) => (
                <Card key={index}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-farm-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-farm-primary">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-semibold">{product.name}</p>
                        <p className="text-sm text-muted-foreground">{t('analytics.unitsSold', { count: product.units })}</p>
                      </div>
                    </div>
                    <p className="font-bold text-farm-primary">{formatCurrency(product.sales)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className="p-4 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold">{t('analytics.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('analytics.subtitle')}</p>
        </div>
      </div>

      {renderBody()}
    </div>
  );
}
